import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  Col,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import { legendsRepo, uploadFile } from "data/repositories/legendsRepo";
import { auth, db } from "data/firebase";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getAuthUser, setAuthUser } from "components/Auth/authStore";
import {
  toE164TR,
  toFriendlyPhoneAuthError,
  usePhoneOtpFirebase,
} from "components/Auth/usePhoneOtpFirebase";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";

const APP_BASE_RAW = process.env.PUBLIC_URL || "";
const APP_BASE =
  APP_BASE_RAW && APP_BASE_RAW !== "/" ? APP_BASE_RAW.replace(/\/+$/, "") : "";

const COUNTRY_OPTIONS = [
  "TR",
  ...getCountries().filter((c) => c !== "TR").sort(),
];

const KARADENIZ_ILLERI = {
  Artvin: ["Merkez","Ardanuç","Arhavi","Borçka","Hopa","Murgul","Şavşat","Yusufeli"],
  Bartın: ["Merkez","Amasra","Kurucaşile","Ulus"],
  Bayburt: ["Merkez","Aydıntepe","Demirözü"],
  Bolu: ["Merkez","Dörtdivan","Gerede","Göynük","Kıbrıscık","Mengen","Mudurnu","Seben","Yeniçağa"],
  Düzce: ["Merkez","Akçakoca","Cumayeri","Çilimli","Gölyaka","Gümüşova","Kaynaşlı","Yığılca"],
  Giresun: ["Merkez","Alucra","Bulancak","Çamoluk","Çanakçı","Dereli","Doğankent","Espiye","Eynesil","Görele","Güce","Keşap","Piraziz","Şebinkarahisar","Tirebolu","Yağlıdere"],
  Gümüşhane: ["Merkez","Kelkit","Köse","Kürtün","Şiran","Torul"],
  Karabük: ["Merkez","Eflani","Eskipazar","Ovacık","Safranbolu","Yenice"],
  Kastamonu: ["Merkez","Abana","Ağlı","Araç","Azdavay","Bozkurt","Cide","Çatalzeytin","Daday","Devrekani","Doğanyurt","Hanönü","İhsangazi","İnebolu","Küre","Pınarbaşı","Seydiler","Şenpazar","Taşköprü","Tosya"],
  Ordu: ["Merkez (Altınordu)","Akkuş","Aybastı","Çamaş","Çatalpınar","Çaybaşı","Fatsa","Gölköy","Gülyalı","Gürgentepe","İkizce","Kabadüz","Kabataş","Korgan","Kumru","Mesudiye","Perşembe","Ulubey","Ünye"],
  Rize: ["Merkez","Ardeşen","Çamlıhemşin","Çayeli","Derepazarı","Fındıklı","Güneysu","Hemşin","İkizdere","İyidere","Kalkandere","Pazar"],
  Samsun: ["Merkez (İlkadım)","Alaçam","Asarcık","Atakum","Ayvacık","Bafra","Canik","Çarşamba","Havza","Kavak","Ladik","Salıpazarı","Tekkeköy","Terme","Vezirköprü","Yakakent","19 Mayıs"],
  Sinop: ["Merkez","Ayancık","Boyabat","Dikmen","Durağan","Erfelek","Gerze","Saraydüzü","Türkeli"],
  Tokat: ["Merkez","Almus","Artova","Başçiftlik","Erbaa","Niksar","Pazar","Reşadiye","Sulusaray","Turhal","Yeşilyurt","Zile"],
  Trabzon: ["Merkez (Ortahisar)","Akçaabat","Araklı","Arsin","Beşikdüzü","Çarşıbaşı","Çaykara","Dernekpazarı","Düzköy","Hayrat","Köprübaşı","Maçka","Of","Sürmene","Şalpazarı","Tonya","Vakfıkebir","Yomra"],
  Zonguldak: ["Merkez","Alaplı","Çaycuma","Devrek","Ereğli","Gökçebey","Kilimli","Kozlu"],
  Amasya: ["Merkez","Göynücek","Gümüşhacıköy","Hamamözü","Merzifon","Suluova","Taşova"],
  Çorum: ["Merkez","Alaca","Bayat","Boğazkale","Dodurga","İskilip","Kargı","Laçin","Mecitözü","Oğuzlar","Ortaköy","Osmancık","Sungurlu","Uğurludağ"],
};

const IL_LISTESI = Object.keys(KARADENIZ_ILLERI).sort((a, b) => a.localeCompare(b, "tr"));

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [];
for (let y = currentYear; y >= 0; y--) {
  YEAR_OPTIONS.push(y);
}

const FILE_SIZE_LIMITS = {
  image: 5,
  audio: 10,
  video: 50,
};

const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

function emptyLegend() {
  return {
    title: "",
    story: "",
    province: "",
    district: "",
    village: "",
    periodYear: "",
    imageUrl: null,
    audioUrl: null,
    videoUrl: null,
    videoLink: "",
    viewCount: 0,
    approved: false,
  };
}

export default function LegendEdit({ embedded = false, onSaved, readOnly = false } = {}) {
  const navigate = useNavigate();
  const params = useParams();
  const legendId = params.id;
  const isNew = legendId === "new" || !legendId;

  const localUser = getAuthUser();
  const currentUid = auth.currentUser?.uid || localUser?.id || "";
  const hasAuthUser = Boolean(auth.currentUser?.uid || localUser?.id);
  const isAnonymousSubmission = !hasAuthUser;
  const [currentRole, setCurrentRole] = useState(localUser?.role || "editor");

  const localRole = String(localUser?.role || "").toLowerCase();
  const localProvider = String(localUser?.provider || "").toLowerCase();
  const isGuestUser = localRole === "guest" || localProvider === "guest";

  const [otpCountry, setOtpCountry] = useState("TR");
  const [otpNationalNumber, setOtpNationalNumber] = useState("");
  const [otpSmsCode, setOtpSmsCode] = useState("");
  const [otpStep, setOtpStep] = useState("phone");
  const [otpError, setOtpError] = useState("");
  const [otpIsSmsLoading, setOtpIsSmsLoading] = useState(false);
  const [otpIsVerifyLoading, setOtpIsVerifyLoading] = useState(false);

  const phoneOtp = usePhoneOtpFirebase({ auth, recaptchaButtonId: "legend-phone-otp-button" });

  const otpDialCode = (() => {
    try {
      return `+${getCountryCallingCode(otpCountry)}`;
    } catch {
      return "+90";
    }
  })();

  const otpPhoneE164 = toE164TR({ dialCode: otpDialCode, nationalNumber: otpNationalNumber });
  const showGuestPhoneVerification = Boolean(!embedded && isNew && (!hasAuthUser || isGuestUser));

  const [form, setForm] = useState(emptyLegend());
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approvalUpdating, setApprovalUpdating] = useState(false);
  const [isSavedInfoOpen, setIsSavedInfoOpen] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [authorInfo, setAuthorInfo] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  const [publicShare, setPublicShare] = useState({
    fullName: false,
    phone: false,
    email: false,
    address: false,
  });
  const lastPublicShareInitLegendIdRef = useRef(null);
  const publicShareDirtyRef = useRef(false);

  const isAdmin = String(currentRole || "").toLowerCase() === "admin";
  const canEditBase =
    isNew ||
    isAdmin ||
    (!!form.createdBy?.uid && !!currentUid && form.createdBy.uid === currentUid);
  const canEdit = !readOnly && canEditBase;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUid || String(currentUid).startsWith("phone:")) return;
      try {
        const snap = await getDoc(doc(db, "users", currentUid));
        const role = snap.exists() ? snap.data()?.role : null;
        if (!cancelled && role) setCurrentRole(role);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUid]);

  useEffect(() => {
    setError("");
    setSuccess("");
    if (isNew) {
      setForm(emptyLegend());
      setRulesAccepted(false);
      lastPublicShareInitLegendIdRef.current = null;
      publicShareDirtyRef.current = false;
      return;
    }

    const loadLegend = async () => {
      setLoading(true);
      try {
        const current = await legendsRepo.getById(legendId);
        if (!current) {
          setError("Efsane bulunamadı");
          return;
        }

        let nextViewCount = Number(current.viewCount);
        nextViewCount = Number.isFinite(nextViewCount) && nextViewCount > 0 ? nextViewCount : 0;
        if (readOnly) {
          await legendsRepo.incrementViewCount(legendId);
          nextViewCount += 1;
        }

        const approved = current.approved === true;

        setForm({
          ...current,
          videoLink: current.videoLink || "",
          viewCount: nextViewCount,
          approved,
        });
      } catch (err) {
        setError("Efsane yüklenirken hata oluştu");
      } finally {
        setLoading(false);
      }
    };

    loadLegend();
  }, [isNew, legendId, readOnly]);
  useEffect(() => {
    if (isNew) return;
    if (!form?.id) return;
    if (lastPublicShareInitLegendIdRef.current === form.id) return;

    const pa = form?.publicAuthor && typeof form.publicAuthor === "object" ? form.publicAuthor : null;
    setPublicShare({
      fullName: Boolean(pa?.fullName),
      phone: Boolean(pa?.phone),
      email: Boolean(pa?.email),
      address: Boolean(pa?.address),
    });

    publicShareDirtyRef.current = false;

    lastPublicShareInitLegendIdRef.current = form.id;
  }, [isNew, form?.id, form?.publicAuthor]);

  const onApprovedChange = async (next) => {
    const safeNext = Boolean(next);
    if (!isAdmin || isNew || !legendId) return;

    setApprovalUpdating(true);
    setError("");
    try {
      await legendsRepo.update(legendId, { approved: safeNext }, {}, {});
      setForm((p) => ({ ...p, approved: safeNext }));
    } catch (err) {
      console.error("Approval update error:", err);
      setError("Onay bilgisi güncellenemedi");
    } finally {
      setApprovalUpdating(false);
    }
  };

  useEffect(() => {
    if (!form.createdBy?.uid) {
      setAuthorInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "userProfiles", form.createdBy.uid));
        if (!cancelled && snap.exists()) {
          setAuthorInfo({ ...form.createdBy, ...snap.data() });
        } else if (!cancelled) {
          setAuthorInfo(form.createdBy);
        }
      } catch {
        if (!cancelled) setAuthorInfo(form.createdBy);
      }
    })();
    return () => { cancelled = true; };
  }, [form.createdBy]);

  useEffect(() => {
    if (!embedded || !isNew) {
      setCurrentUserProfile(null);
      return;
    }

    let cancelled = false;
    (async () => {
      if (!currentUid || String(currentUid).startsWith("phone:")) return;
      try {
        const snap = await getDoc(doc(db, "userProfiles", currentUid));
        if (!cancelled && snap.exists()) {
          setCurrentUserProfile(snap.data() || {});
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [embedded, isNew, currentUid]);

  useEffect(() => {
    if (!showGuestPhoneVerification) return;
    if (otpStep !== "phone") return;

    (async () => {
      try {
        await phoneOtp.initRecaptcha();
      } catch {}
    })();
  }, [showGuestPhoneVerification, otpStep, phoneOtp]);

  const phoneKeyDigits = (value) => String(value ?? "").replace(/[^0-9]/g, "");

  const onOtpSendSms = async () => {
    setOtpError("");
    if (!otpPhoneE164) {
      setOtpError("Lütfen telefon numaranızı girin.");
      return;
    }

    setOtpIsSmsLoading(true);
    try {
      await phoneOtp.sendCode(otpPhoneE164);
      setOtpStep("code");
    } catch (e) {
      console.error("LegendEdit phone auth sendSms error:", e);
      const code = e?.code ? String(e.code) : "";
      if (
        code.includes("captcha") ||
        code.includes("invalid-app-credential") ||
        code.includes("invalid-recaptcha-token") ||
        code.includes("auth/internal-error")
      ) {
        try {
          await phoneOtp.resetRecaptcha();
        } catch {}
      }
      setOtpError(`${toFriendlyPhoneAuthError(e)}${code ? ` (Hata: ${code})` : ""}`);
    } finally {
      setOtpIsSmsLoading(false);
    }
  };

  const onOtpVerifyCode = async () => {
    setOtpError("");
    const code = String(otpSmsCode ?? "").trim();
    if (!code) {
      setOtpError("Lütfen SMS doğrulama kodunu girin.");
      return;
    }

    setOtpIsVerifyLoading(true);
    try {
      const credential = await phoneOtp.verifyCode(code);
      const user = credential?.user;
      const e164 = user?.phoneNumber || otpPhoneE164;
      const uid = user?.uid;

      if (uid) {
        try {
          const byPhoneId = phoneKeyDigits(e164);
          if (byPhoneId) {
            const byPhoneRef = doc(db, "usersByPhone", byPhoneId);
            const byPhoneSnap = await getDoc(byPhoneRef);
            if (!byPhoneSnap.exists()) {
              await setDoc(
                byPhoneRef,
                { uid, phone: e164, createdAt: serverTimestamp() },
                { merge: true }
              );
            }
          }

          const userRef = doc(db, "users", uid);
          await setDoc(
            userRef,
            {
              uid,
              phone: e164,
              provider: "phone",
              lastLoginAt: serverTimestamp(),
            },
            { merge: true }
          );

          let role = "editor";
          try {
            const userSnap = await getDoc(userRef);
            const existingRole = userSnap.exists() ? userSnap.data()?.role : null;
            if (existingRole) {
              role = existingRole;
            } else {
              await setDoc(userRef, { role: "editor" }, { merge: true });
            }
          } catch {}

          setCurrentRole(role);
          setAuthUser({
            id: uid,
            provider: "phone",
            phone: e164,
            role,
            createdAt: new Date().toISOString(),
          });
        } catch (e) {
          console.error("LegendEdit phone auth user doc update error:", e);
          setAuthUser({
            id: uid,
            provider: "phone",
            phone: e164,
            role: "editor",
            createdAt: new Date().toISOString(),
          });
        }
      } else {
        setAuthUser({
          id: uid || `phone:${e164}`,
          provider: "phone",
          phone: e164,
          role: "editor",
          createdAt: new Date().toISOString(),
        });
      }

      setOtpStep("verified");
      setOtpSmsCode("");
      setOtpNationalNumber("");
      try {
        phoneOtp.clearRecaptcha();
      } catch {}
    } catch (e) {
      const errCode = e?.code ? String(e.code) : "";
      setOtpError(
        `Kod doğrulanamadı. Lütfen tekrar deneyin.${errCode ? ` (Hata: ${errCode})` : ""}`
      );
    } finally {
      setOtpIsVerifyLoading(false);
    }
  };

  const uploadNow = async (type, selectedFile) => {
    let setUploading, folder, urlKey, clearSelectedFile, inputRef;

    switch (type) {
      case "image":
        setUploading = setUploadingImage;
        folder = "legends/images";
        urlKey = "imageUrl";
        clearSelectedFile = () => setImageFile(null);
        inputRef = imageInputRef;
        break;
      case "audio":
        setUploading = setUploadingAudio;
        folder = "legends/audio";
        urlKey = "audioUrl";
        clearSelectedFile = () => setAudioFile(null);
        inputRef = audioInputRef;
        break;
      case "video":
        setUploading = setUploadingVideo;
        folder = "legends/videos";
        urlKey = "videoUrl";
        clearSelectedFile = () => setVideoFile(null);
        inputRef = videoInputRef;
        break;
      default:
        return;
    }

    if (!selectedFile) {
      setError(
        `Lütfen önce bir ${
          type === "image" ? "görsel" : type === "audio" ? "ses" : "video"
        } dosyası seçin.`
      );
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      const downloadURL = await uploadFile(selectedFile, folder);
      setForm((p) => ({ ...p, [urlKey]: downloadURL }));
      clearSelectedFile();
      if (inputRef?.current) inputRef.current.value = "";
      setSuccess(
        `${
          type === "image" ? "Görsel" : type === "audio" ? "Ses" : "Video"
        } başarıyla yüklendi!`
      );
    } catch (err) {
      console.error("Upload error:", err);
      setError(
        `${
          type === "image" ? "Görsel" : type === "audio" ? "Ses" : "Video"
        } yüklenirken hata oluştu.`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = async (type, file, options = {}) => {
    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);
    const limit = FILE_SIZE_LIMITS[type];

    if (fileSizeMB > limit) {
      setError(
        `${
          type === "image" ? "Görsel" : type === "audio" ? "Ses" : "Video"
        } dosyası çok büyük! Maksimum ${limit} MB olmalıdır. (Seçilen: ${bytesToMB(
          file.size
        )} MB)`
      );
      return;
    }

    setError("");
    setSuccess("");

    switch (type) {
      case "image":
        setImageFile(file);
        break;
      case "audio":
        setAudioFile(file);
        break;
      case "video":
        setVideoFile(file);
        break;
      default:
        break;
    }

    if (options.autoUpload) {
      await uploadNow(type, file);
    }
  };

  const handleUpload = async (type) => {
    const selectedFile =
      type === "image" ? imageFile : type === "audio" ? audioFile : videoFile;
    await uploadNow(type, selectedFile);
  };

  const removeFile = (type) => {
    setSuccess("");
    switch (type) {
      case "image":
        setImageFile(null);
        setForm((p) => ({ ...p, imageUrl: null }));
        if (imageInputRef.current) imageInputRef.current.value = "";
        break;
      case "audio":
        setAudioFile(null);
        setForm((p) => ({ ...p, audioUrl: null }));
        if (audioInputRef.current) audioInputRef.current.value = "";
        break;
      case "video":
        setVideoFile(null);
        setForm((p) => ({ ...p, videoUrl: null }));
        if (videoInputRef.current) videoInputRef.current.value = "";
        break;
      default:
        break;
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (isNew && !readOnly && !rulesAccepted) {
      setError("Devam etmek için 'Yeni Efsane Ekleme Kuralları'nı okuduğunuzu onaylamalısınız.");
      return;
    }

    if (!isNew && !canEdit) {
      setError("Bu efsaneyi sadece ekleyen kullanıcı veya admin düzenleyebilir.");
      return;
    }

    if (!form.title.trim()) {
      setError("Efsane başlığı zorunludur");
      return;
    }

    if (!form.story.trim()) {
      setError("Hikaye zorunludur");
      return;
    }

    setSaving(true);

    try {
      const legendData = {
        title: form.title,
        story: form.story,
        province: form.province || "",
        district: form.district || "",
        village: form.village || "",
        periodYear: form.periodYear || "",
        imageUrl: form.imageUrl,
        audioUrl: form.audioUrl,
        videoUrl: form.videoUrl,
        videoLink: form.videoLink || "",
      };

      if (imageFile && !legendData.imageUrl) {
        setUploadingImage(true);
        try {
          legendData.imageUrl = await uploadFile(imageFile, "legends/images");
          setForm((p) => ({ ...p, imageUrl: legendData.imageUrl }));
          setImageFile(null);
          if (imageInputRef.current) imageInputRef.current.value = "";
        } finally {
          setUploadingImage(false);
        }
      }

      if (audioFile && !legendData.audioUrl) {
        setUploadingAudio(true);
        try {
          legendData.audioUrl = await uploadFile(audioFile, "legends/audio");
          setForm((p) => ({ ...p, audioUrl: legendData.audioUrl }));
          setAudioFile(null);
          if (audioInputRef.current) audioInputRef.current.value = "";
        } finally {
          setUploadingAudio(false);
        }
      }

      if (videoFile && !legendData.videoUrl) {
        setUploadingVideo(true);
        try {
          legendData.videoUrl = await uploadFile(videoFile, "legends/videos");
          setForm((p) => ({ ...p, videoUrl: legendData.videoUrl }));
          setVideoFile(null);
          if (videoInputRef.current) videoInputRef.current.value = "";
        } finally {
          setUploadingVideo(false);
        }
      }

      if (isNew) {
        const currentUser = auth.currentUser;
        legendData.createdBy = {
          uid: isAnonymousSubmission ? "anonymous" : currentUser?.uid || localUser?.id || "",
          anonymous: isAnonymousSubmission,
        };

        if (!isAdmin) {
          legendData.isActive = false;
          legendData.approved = false;
        }

        if (isAnonymousSubmission) {
          legendData.submittedAnonymously = true;
        }

        if (embedded) {
          const nameValue =
            currentUserProfile?.fullName ||
            currentUser?.displayName ||
            localUser?.displayName ||
            "";
          const phoneValue =
            currentUser?.phoneNumber ||
            currentUserProfile?.phone ||
            localUser?.phone ||
            "";
          const emailValue =
            currentUserProfile?.email ||
            currentUser?.email ||
            localUser?.email ||
            "";
          const addressValue = currentUserProfile?.address || "";

          const publicAuthor = {};
          if (publicShare.fullName && nameValue) publicAuthor.fullName = nameValue;
          if (publicShare.phone && phoneValue) publicAuthor.phone = phoneValue;
          if (publicShare.email && emailValue) publicAuthor.email = emailValue;
          if (publicShare.address && addressValue) publicAuthor.address = addressValue;

          if (Object.keys(publicAuthor).length > 0) {
            legendData.publicAuthor = publicAuthor;
          }
        }

        if (embedded) {
          legendData.isActive = false;
        }
        await legendsRepo.create(legendData, {});

        if (!embedded) {
          setIsSavedInfoOpen(true);
          return;
        }
      } else {
        if (canEdit && publicShareDirtyRef.current) {
          const existingPublicAuthor =
            form?.publicAuthor && typeof form.publicAuthor === "object" ? form.publicAuthor : null;
          const nameValue =
            authorInfo?.fullName ||
            authorInfo?.displayName ||
            authorInfo?.name ||
            existingPublicAuthor?.fullName ||
            "";
          const phoneValue = authorInfo?.phone || authorInfo?.phoneNumber || existingPublicAuthor?.phone || "";
          const emailValue = authorInfo?.email || existingPublicAuthor?.email || "";
          const addressValue = authorInfo?.address || existingPublicAuthor?.address || "";

          const publicAuthor = {};
          if (publicShare.fullName && nameValue) publicAuthor.fullName = nameValue;
          if (publicShare.phone && phoneValue) publicAuthor.phone = phoneValue;
          if (publicShare.email && emailValue) publicAuthor.email = emailValue;
          if (publicShare.address && addressValue) publicAuthor.address = addressValue;

          if (Object.keys(publicAuthor).length > 0) {
            legendData.publicAuthor = publicAuthor;
          } else {
            legendData.publicAuthor = null;
          }
        }
        await legendsRepo.update(legendId, legendData, {}, {});
      }

      if (embedded) {
        setSuccess("Efsane kaydedildi.");
        setForm(emptyLegend());
        setImageFile(null);
        setAudioFile(null);
        setVideoFile(null);
        if (imageInputRef.current) imageInputRef.current.value = "";
        if (audioInputRef.current) audioInputRef.current.value = "";
        if (videoInputRef.current) videoInputRef.current.value = "";
        onSaved?.();
        return;
      }

      navigate("/admin/content/legends");
    } catch (err) {
      console.error("Save error:", err);
      setError(err?.message || "Kaydetme hatası oluştu");
    } finally {
      setSaving(false);
    }
  };

  const handleSavedInfoClose = () => {
    setIsSavedInfoOpen(false);
    navigate(`${APP_BASE}/admin/index`, { replace: true });
  };

  const isUploading = uploadingImage || uploadingAudio || uploadingVideo;

  const onHideLegend = async () => {
    if (!legendId) return;
    const ok = window.confirm(
      "Bu efsane gizlensin mi? Bu işlem veritabanından silmez; sadece pasif hale getirir."
    );
    if (!ok) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await legendsRepo.hide(legendId, {
        uid: currentUid || "",
        role: currentRole || "",
      });
      navigate("/admin/content/legends");
    } catch (err) {
      console.error("Hide error:", err);
      setError("Efsane gizlenirken hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <div className="header bg-gradient-info pb-6 pt-5 pt-md-7">
      <Container fluid>
        <div className="header-body">
          <Row className="align-items-center py-4">
            <Col lg="8" xs="12">
              <h6 className="h2 text-white d-inline-block mb-0">
                {isNew ? "Yeni Efsane" : readOnly ? "Efsane İncele" : "Efsane Düzenle"}
              </h6>
              {!isNew && (
                <div className="mt-2">
                  <small className="text-white-50">
                    Okunma: <strong className="text-white">{Number(form.viewCount) || 0}</strong>
                  </small>
                </div>
              )}
            </Col>
            <Col lg="4" xs="12" className="text-right">
              <Button
                tag={Link}
                to="/admin/content/legends"
                color="secondary"
                size="sm"
              >
                Listeye Dön
              </Button>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
  const showPersonalInfoCard =
    showGuestPhoneVerification ||
    (embedded && isNew) ||
    (!embedded && !isNew && canEdit);

  const showLegendRulesConsent = Boolean(!embedded && isNew && !readOnly);

  const legendRulesContent = (
    <div className="text-muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
      <p className="mb-2">
        Bu bölüm, efsane paylaşımı için temel kuralları ve kişisel verilerin işlenmesine ilişkin kısa
        bilgilendirmeyi içerir. Efsaneler anlatı niteliğindedir; birebir doğrulanmış olması beklenmez.
        Ancak paylaştığınız içerik hukuka uygun olmalı ve kimseyi hedef göstermemelidir.
      </p>
      <ul className="mb-2" style={{ paddingLeft: 18 }}>
        <li>
          <strong>Paylaşım sorumluluğu:</strong> Paylaştığınız metin, görsel, ses ve video içeriklerinin
          paylaşım hakkına sahip olduğunuzdan ve içeriğin hukuka uygun olduğundan siz sorumlusunuz.
        </li>
        <li>
          <strong>Telif ve izin:</strong> Yüklediğiniz/eklediğiniz içerikler size ait olmalı veya paylaşım için
          gerekli izinlere sahip olmalısınız (mümkünse kaynak belirtin).
        </li>
        <li>
          <strong>Kişilik hakları ve mahremiyet:</strong> Gerçek kişileri hedef gösteren iddialar, iftira veya
          özel hayatı ihlal eden içerikler paylaşmayın. Başkalarına ait telefon, adres, T.C. kimlik numarası,
          sağlık bilgisi gibi kişisel verileri paylaşmayın.
        </li>
        <li>
          <strong>Saygılı dil:</strong> Hakaret, nefret söylemi, tehdit, şiddet, cinsel içerik veya yasa dışı
          faaliyetleri teşvik eden içerikler yayınlanmaz.
        </li>
        <li>
          <strong>Moderasyon:</strong> Gönderiler yönetici incelemesinden geçer; uygun görülmeyen içerikler
          yayınlanmayabilir, düzenlenebilir veya kaldırılabilir.
        </li>
        <li>
          <strong>KVKK bilgilendirmesi:</strong> Gönderdiğiniz içerik ve varsa eklediğiniz medya; inceleme,
          yayınlama, site güvenliği, suistimalin önlenmesi ve iletişim süreçleri kapsamında işlenebilir ve
          saklanabilir. Telefon doğrulaması yaparsanız, bu bilgi hesabınızı ilişkilendirmek amacıyla kullanılır
          ve varsayılan olarak herkese açık gösterilmez.
        </li>
        <li>
          <strong>Haklarınız:</strong> KVKK kapsamındaki haklarınızı kullanmak veya içerik kaldırma/silme talebinde
          bulunmak için site yönetimiyle iletişime geçebilirsiniz.
        </li>
      </ul>
      <p className="mb-0">
        Bu kuralları onaylayarak gönderinizi paylaşmayı ve yukarıdaki bilgilendirme kapsamında verilerinizin
        işlenebileceğini anladığınızı beyan edersiniz.
      </p>
    </div>
  );
  const personalInfoPreview = {
    fullName: isNew
      ? currentUserProfile?.fullName || auth.currentUser?.displayName || localUser?.displayName || "-"
      : authorInfo?.fullName || authorInfo?.displayName || authorInfo?.name || "-",
    phone: isNew
      ? auth.currentUser?.phoneNumber || currentUserProfile?.phone || localUser?.phone || "-"
      : authorInfo?.phone || authorInfo?.phoneNumber || "-",
    email: isNew
      ? currentUserProfile?.email || auth.currentUser?.email || localUser?.email || "-"
      : authorInfo?.email || "-",
    address: isNew
      ? currentUserProfile?.address || "-"
      : authorInfo?.address || "-",
  };

  if (loading) {
    return (
      <>
        {!embedded && header}
        <Container className={embedded ? "mt-4" : "mt--6"} fluid>
          <Row>
            <Col>
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 mb-0 text-muted">Yükleniyor...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      {!embedded && header}

      <Container className={embedded ? "mt-4" : "mt--6"} fluid>
        <Row>
          <Col xl={isNew ? "12" : "8"}>
            <Card className="shadow">
              <CardHeader className="border-0 overflow-hidden">
                {!embedded && !isNew && isAdmin ? (
                  <div className="mb-3">
                    <div
                      className={
                        `d-flex align-items-center justify-content-start rounded px-3 py-3 position-relative ` +
                        (form.approved
                          ? "bg-success text-white"
                          : "bg-danger text-white")
                      }
                    >
                      <Label
                        className="mb-0 d-flex align-items-start w-100"
                        style={{ cursor: approvalUpdating ? "not-allowed" : "pointer" }}
                      >
                        <Input
                          type="checkbox"
                          checked={Boolean(form.approved)}
                          onChange={(e) => onApprovedChange(Boolean(e?.target?.checked))}
                          disabled={approvalUpdating}
                          className="mr-3"
                          style={{ width: 18, height: 18, marginTop: 2, flex: "0 0 auto" }}
                        />
                        <strong style={{ maxWidth: "100%", wordBreak: "break-word" }}>
                          İÇERİK İNCELENDİ VE ONAYLANDI
                        </strong>
                      </Label>

                      {approvalUpdating ? (
                        <Spinner
                          size="sm"
                          color="light"
                          style={{ position: "absolute", right: 12 }}
                        />
                      ) : null}
                    </div>
                  </div>
                ) : null}
                <h3 className={embedded || isNew ? "mb-0 text-center" : "mb-0"}>
                  {embedded || isNew ? (
                    <>
                      <span className="d-block d-md-inline">
                        Yeni Efsaneyi Aşağıdan
                      </span>
                      <span className="d-none d-md-inline"> </span>
                      <span className="d-block d-md-inline">Yazabilirsiniz</span>
                    </>
                  ) : (
                    "Efsane Bilgileri"
                  )}
                </h3>
              </CardHeader>
              <CardBody>
                {!readOnly && !isNew && !canEdit && (
                  <Alert color="warning">
                    Bu efsaneyi sadece ekleyen kullanıcı veya admin düzenleyebilir.
                  </Alert>
                )}
                {error && <Alert color="danger">{error}</Alert>}
                {success && <Alert color="success">{success}</Alert>}

                <Form onSubmit={onSave}>
                  <fieldset disabled={!canEdit || saving || isUploading}>
                  <FormGroup>
                    <Label>
                      {isNew ? "Yeni Efsane Başlığı" : "Efsane Başlığı"}{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      value={form.title}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, title: e.target.value }))
                      }
                      placeholder="Efsane başlığını girin..."
                      disabled={saving || isUploading}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Hikaye <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      rows="8"
                      value={form.story}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, story: e.target.value }))
                      }
                      placeholder="Efsanenin hikayesini yazın..."
                      disabled={saving || isUploading}
                    />
                  </FormGroup>

                  <hr className="my-4" />

                  <h6 className="heading-small text-muted mb-3">
                    <i className="ni ni-pin-3 mr-1" />
                    Konum ve Dönem Bilgileri
                  </h6>

                  <Row>
                    <Col md="3" sm="6" xs={embedded ? "6" : undefined}>
                      <FormGroup>
                        <Label>İl</Label>
                        <Input
                          type="select"
                          bsSize={embedded ? "sm" : undefined}
                          style={embedded ? { fontSize: 16 } : undefined}
                          value={form.province}
                          onChange={(e) => {
                            const val = e.target.value;
                            setForm((p) => ({ ...p, province: val, district: "" }));
                          }}
                          disabled={saving || isUploading}
                        >
                          <option value="">Seçiniz</option>
                          {IL_LISTESI.map((il) => (
                            <option key={il} value={il}>
                              {il}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3" sm="6" xs={embedded ? "6" : undefined}>
                      <FormGroup>
                        <Label>İlçe (Opsiyonel)</Label>
                        <Input
                          type="select"
                          bsSize={embedded ? "sm" : undefined}
                          style={embedded ? { fontSize: 16 } : undefined}
                          value={form.district}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, district: e.target.value }))
                          }
                          disabled={saving || isUploading || !form.province}
                        >
                          <option value="">Seçiniz (Opsiyonel)</option>
                          {(KARADENIZ_ILLERI[form.province] || []).map((ilce) => (
                            <option key={ilce} value={ilce}>
                              {ilce}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3" sm="6" xs={embedded ? "6" : undefined}>
                      <FormGroup>
                        <Label>Köy / Mahalle (Opsiyonel)</Label>
                        <Input
                          type="text"
                          bsSize={embedded ? "sm" : undefined}
                          value={form.village}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, village: e.target.value }))
                          }
                          placeholder={embedded ? "Yazınız" : "Köy veya mahalle adı..."}
                          disabled={saving || isUploading}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3" sm="6" xs={embedded ? "6" : undefined}>
                      <FormGroup>
                        <Label>Efsanenin Dönemi (Yıl) (Opsiyonel)</Label>
                        <Input
                          type="select"
                          bsSize={embedded ? "sm" : undefined}
                          style={embedded ? { fontSize: 16 } : undefined}
                          value={form.periodYear}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, periodYear: e.target.value }))
                          }
                          disabled={saving || isUploading}
                        >
                          <option value="">Seçiniz (Opsiyonel)</option>
                          {YEAR_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  {embedded ? (
                    <Row>
                      <Col xs="12" sm="6" md="4">
                        <FormGroup>
                          <Label>
                            <i className="fas fa-image mr-2 text-success"></i>
                            Görsel Yükle
                          </Label>

                          {form.imageUrl ? (
                            <div className="mb-3 p-3 bg-light rounded">
                              <img
                                src={form.imageUrl}
                                alt="Yüklenen görsel"
                                loading="lazy"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 200,
                                  borderRadius: 8,
                                }}
                              />
                              <div className="mt-2">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeFile("image")}
                                  disabled={saving || isUploading}
                                >
                                  <i className="fas fa-trash mr-1"></i> Görseli Kaldır
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <Input
                                type="file"
                                accept="image/*"
                                ref={imageInputRef}
                                onChange={(e) =>
                                  handleFileSelect(
                                    "image",
                                    e.target.files[0],
                                    { autoUpload: true }
                                  )
                                }
                                disabled={saving || isUploading}
                              />
                              <Button
                                color="success"
                                size="sm"
                                className="mt-2 d-none d-md-inline-block"
                                onClick={() => handleUpload("image")}
                                disabled={!imageFile || saving || isUploading}
                              >
                                {uploadingImage ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <>
                                    <i className="fas fa-upload mr-1"></i> Yükle
                                  </>
                                )}
                              </Button>

                              {uploadingImage && (
                                <div className="mt-2 text-muted">
                                  <Spinner size="sm" className="mr-2" />
                                  Yükleniyor...
                                </div>
                              )}
                            </div>
                          )}

                          {imageFile && !form.imageUrl && (
                            <small className="text-muted mt-1 d-block">
                              Seçilen: {imageFile.name} ({bytesToMB(imageFile.size)} MB)
                            </small>
                          )}
                          <small className="text-muted mt-1 d-block">
                            JPG, PNG, GIF, WebP — Maks. {FILE_SIZE_LIMITS.image} MB
                          </small>
                        </FormGroup>
                      </Col>

                      <Col xs="12" sm="6" md="4">
                        <FormGroup>
                          <Label>
                            <i className="fas fa-music mr-2 text-info"></i>
                            Ses Dosyası Yükle
                          </Label>

                          {form.audioUrl ? (
                            <div className="mb-3 p-3 bg-light rounded">
                              <audio controls style={{ width: "100%" }}>
                                <source src={form.audioUrl} />
                                Tarayıcınız ses oynatmayı desteklemiyor.
                              </audio>
                              <div className="mt-2">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeFile("audio")}
                                  disabled={saving || isUploading}
                                >
                                  <i className="fas fa-trash mr-1"></i> Sesi Kaldır
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <Input
                                type="file"
                                accept="audio/*,audio/mp4,video/mp4,.m4a,.aac,.mp3,.wav,.ogg"
                                ref={audioInputRef}
                                onChange={(e) =>
                                  handleFileSelect(
                                    "audio",
                                    e.target.files[0],
                                    { autoUpload: true }
                                  )
                                }
                                disabled={saving || isUploading}
                              />
                              <Button
                                color="info"
                                size="sm"
                                className="mt-2 d-none d-md-inline-block"
                                onClick={() => handleUpload("audio")}
                                disabled={!audioFile || saving || isUploading}
                              >
                                {uploadingAudio ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <>
                                    <i className="fas fa-upload mr-1"></i> Yükle
                                  </>
                                )}
                              </Button>

                              {uploadingAudio && (
                                <div className="mt-2 text-muted">
                                  <Spinner size="sm" className="mr-2" />
                                  Yükleniyor...
                                </div>
                              )}
                            </div>
                          )}

                          {audioFile && !form.audioUrl && (
                            <small className="text-muted mt-1 d-block">
                              Seçilen: {audioFile.name} ({bytesToMB(audioFile.size)} MB)
                            </small>
                          )}
                          <small className="text-muted mt-1 d-block">
                            MP3, M4A, AAC, WAV, OGG — Maks. {FILE_SIZE_LIMITS.audio} MB
                          </small>
                        </FormGroup>
                      </Col>

                      <Col xs="12" sm="12" md="4">
                        <FormGroup>
                          <Label>
                            <i className="fas fa-video mr-2 text-warning"></i>
                            Video Yükle
                          </Label>

                          {form.videoUrl ? (
                            <div className="mb-3 p-3 bg-light rounded">
                              <video
                                controls
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 300,
                                  borderRadius: 8,
                                }}
                              >
                                <source src={form.videoUrl} />
                                Tarayıcınız video oynatmayı desteklemiyor.
                              </video>
                              <div className="mt-2">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeFile("video")}
                                  disabled={saving || isUploading}
                                >
                                  <i className="fas fa-trash mr-1"></i> Videoyu Kaldır
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <Input
                                type="file"
                                accept="video/*"
                                ref={videoInputRef}
                                onChange={(e) =>
                                  handleFileSelect(
                                    "video",
                                    e.target.files[0],
                                    { autoUpload: true }
                                  )
                                }
                                disabled={saving || isUploading}
                              />
                              <Button
                                color="warning"
                                size="sm"
                                className="mt-2 d-none d-md-inline-block"
                                onClick={() => handleUpload("video")}
                                disabled={!videoFile || saving || isUploading}
                              >
                                {uploadingVideo ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <>
                                    <i className="fas fa-upload mr-1"></i> Yükle
                                  </>
                                )}
                              </Button>

                              {uploadingVideo && (
                                <div className="mt-2 text-muted">
                                  <Spinner size="sm" className="mr-2" />
                                  Yükleniyor...
                                </div>
                              )}
                            </div>
                          )}

                          {videoFile && !form.videoUrl && (
                            <small className="text-muted mt-1 d-block">
                              Seçilen: {videoFile.name} ({bytesToMB(videoFile.size)} MB)
                            </small>
                          )}
                          <small className="text-muted mt-1 d-block">
                            MP4, WebM, MOV — Maks. {FILE_SIZE_LIMITS.video} MB
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <FormGroup>
                        <Label>
                          <i className="fas fa-image mr-2 text-success"></i>
                          Görsel Yükle
                        </Label>

                        {form.imageUrl ? (
                          <div className="mb-3 p-3 bg-light rounded">
                            <img
                              src={form.imageUrl}
                              alt="Yüklenen görsel"
                              style={{
                                maxWidth: "100%",
                                maxHeight: 200,
                                borderRadius: 8,
                              }}
                            />
                            <div className="mt-2">
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeFile("image")}
                                disabled={saving || isUploading}
                              >
                                <i className="fas fa-trash mr-1"></i> Görseli Kaldır
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center">
                            <Input
                              type="file"
                              accept="image/*"
                              ref={imageInputRef}
                              onChange={(e) =>
                                handleFileSelect("image", e.target.files[0])
                              }
                              disabled={saving || isUploading}
                              style={{ maxWidth: 420 }}
                            />
                            <Button
                              color="success"
                              size="sm"
                              className="ml-1"
                              onClick={() => handleUpload("image")}
                              disabled={!imageFile || saving || isUploading}
                            >
                              {uploadingImage ? (
                                <Spinner size="sm" />
                              ) : (
                                <>
                                  <i className="fas fa-upload mr-1"></i> Yükle
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {imageFile && !form.imageUrl && (
                          <small className="text-muted mt-1 d-block">
                            Seçilen: {imageFile.name} ({bytesToMB(imageFile.size)} MB)
                          </small>
                        )}
                        <small className="text-muted mt-1 d-block">
                          JPG, PNG, GIF, WebP — Maks. {FILE_SIZE_LIMITS.image} MB
                        </small>
                      </FormGroup>

                      <FormGroup>
                        <Label>
                          <i className="fas fa-music mr-2 text-info"></i>
                          Ses Dosyası Yükle
                        </Label>

                        {form.audioUrl ? (
                          <div className="mb-3 p-3 bg-light rounded">
                            <audio controls style={{ width: "100%" }}>
                              <source src={form.audioUrl} />
                              Tarayıcınız ses oynatmayı desteklemiyor.
                            </audio>
                            <div className="mt-2">
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeFile("audio")}
                                disabled={saving || isUploading}
                              >
                                <i className="fas fa-trash mr-1"></i> Sesi Kaldır
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center">
                            <Input
                              type="file"
                              accept="audio/*,audio/mp4,video/mp4,.m4a,.aac,.mp3,.wav,.ogg"
                              ref={audioInputRef}
                              onChange={(e) =>
                                handleFileSelect("audio", e.target.files[0])
                              }
                              disabled={saving || isUploading}
                              style={{ maxWidth: 420 }}
                            />
                            <Button
                              color="info"
                              size="sm"
                              className="ml-1"
                              onClick={() => handleUpload("audio")}
                              disabled={!audioFile || saving || isUploading}
                            >
                              {uploadingAudio ? (
                                <Spinner size="sm" />
                              ) : (
                                <>
                                  <i className="fas fa-upload mr-1"></i> Yükle
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {audioFile && !form.audioUrl && (
                          <small className="text-muted mt-1 d-block">
                            Seçilen: {audioFile.name} ({bytesToMB(audioFile.size)} MB)
                          </small>
                        )}
                        <small className="text-muted mt-1 d-block">
                          MP3, M4A, AAC, WAV, OGG — Maks. {FILE_SIZE_LIMITS.audio} MB
                        </small>
                      </FormGroup>

                      <FormGroup>
                        <Label>
                          <i className="fas fa-video mr-2 text-warning"></i>
                          Video Yükle
                        </Label>

                        {form.videoUrl ? (
                          <div className="mb-3 p-3 bg-light rounded">
                            <video
                              controls
                              style={{
                                maxWidth: "100%",
                                maxHeight: 300,
                                borderRadius: 8,
                              }}
                            >
                              <source src={form.videoUrl} />
                              Tarayıcınız video oynatmayı desteklemiyor.
                            </video>
                            <div className="mt-2">
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeFile("video")}
                                disabled={saving || isUploading}
                              >
                                <i className="fas fa-trash mr-1"></i> Videoyu Kaldır
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center">
                            <Input
                              type="file"
                              accept="video/*"
                              ref={videoInputRef}
                              onChange={(e) =>
                                handleFileSelect("video", e.target.files[0])
                              }
                              disabled={saving || isUploading}
                              style={{ maxWidth: 420 }}
                            />
                            <Button
                              color="warning"
                              size="sm"
                              className="ml-1"
                              onClick={() => handleUpload("video")}
                              disabled={!videoFile || saving || isUploading}
                            >
                              {uploadingVideo ? (
                                <Spinner size="sm" />
                              ) : (
                                <>
                                  <i className="fas fa-upload mr-1"></i> Yükle
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {videoFile && !form.videoUrl && (
                          <small className="text-muted mt-1 d-block">
                            Seçilen: {videoFile.name} ({bytesToMB(videoFile.size)} MB)
                          </small>
                        )}
                        <small className="text-muted mt-1 d-block">
                          MP4, WebM, MOV — Maks. {FILE_SIZE_LIMITS.video} MB
                        </small>
                      </FormGroup>
                    </>
                  )}

                  <FormGroup>
                    <Label>
                      <i className="fas fa-link mr-2 text-primary"></i>
                      Video Linki (Opsiyonel)
                    </Label>
                    <Input
                      type="text"
                      value={form.videoLink}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, videoLink: e.target.value }))
                      }
                      placeholder="YouTube, Vimeo vb. video linki yapıştırın..."
                      disabled={saving || isUploading}
                    />
                    <small className="text-muted">
                      Video dosyası yüklemek yerine YouTube, Vimeo gibi
                      platformlardan link ekleyebilirsiniz.
                    </small>
                  </FormGroup>

                  {showPersonalInfoCard ? (
                    <div className="border rounded p-3 mb-4 bg-white">
                      <h6 className="heading-small text-muted mb-3">
                        <i className="ni ni-single-02 mr-1" />
                        Kişisel Bilgiler (Opsiyonel)
                      </h6>

                      {showGuestPhoneVerification ? (
                        <>
                          <div
                            className="text-center mb-3 rounded p-2"
                            style={{ backgroundColor: "#fff3cd", border: "1px solid #ffc107" }}
                          >
                            <small className="font-weight-bold text-dark">
                              Efsane Detayında Bilgilerinizin Görülmesini İsterseniz Üye Olup Düzenleyebilirsiniz, Üye Değilseniz Yüklediğiniz Efsane ANONİM Olarak Yayınlanacaktır.
                            </small>
                          </div>

                          {otpError ? (
                            <div className="text-danger mb-3" style={{ wordBreak: "break-word" }}>
                              <small>{otpError}</small>
                            </div>
                          ) : null}

                          {otpStep === "verified" ? (
                            <div className="text-success mb-3">
                              <small>Telefon doğrulandı.</small>
                            </div>
                          ) : null}

                          {otpStep === "phone" ? (
                            <FormGroup className="mb-2">
                              <InputGroup className="input-group-alternative">
                                <InputGroupAddon addonType="prepend">
                                  <InputGroupText>
                                    <i className="ni ni-mobile-button mr-2" />
                                    <select
                                      className="bg-transparent border-0"
                                      aria-label="Ülke kodu"
                                      value={otpCountry}
                                      onChange={(e) => setOtpCountry(e.target.value)}
                                    >
                                      {COUNTRY_OPTIONS.map((c) => (
                                        <option key={c} value={c}>
                                          {c} (+{getCountryCallingCode(c)})
                                        </option>
                                      ))}
                                    </select>
                                  </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                  placeholder="530 XXX XX XX"
                                  type="tel"
                                  inputMode="tel"
                                  autoComplete="tel"
                                  value={otpNationalNumber}
                                  onChange={(e) => setOtpNationalNumber(e.target.value)}
                                  disabled={otpIsSmsLoading || otpIsVerifyLoading || saving || isUploading}
                                />
                              </InputGroup>
                            </FormGroup>
                          ) : (
                            <FormGroup className="mb-2">
                              <InputGroup className="input-group-alternative">
                                <InputGroupAddon addonType="prepend">
                                  <InputGroupText>
                                    <i className="ni ni-key-25" />
                                  </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                  placeholder="SMS Kodu"
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="one-time-code"
                                  value={otpSmsCode}
                                  onChange={(e) => setOtpSmsCode(e.target.value)}
                                  disabled={otpIsSmsLoading || otpIsVerifyLoading || saving || isUploading}
                                />
                              </InputGroup>
                            </FormGroup>
                          )}

                          {otpStep !== "verified" ? (
                            <div
                              className="mt-2"
                              style={{ display: "flex", flexWrap: "wrap", gap: 10 }}
                            >
                              <Button
                                id={otpStep === "phone" ? "legend-phone-otp-button" : undefined}
                                color="primary"
                                type="button"
                                onClick={otpStep === "phone" ? onOtpSendSms : onOtpVerifyCode}
                                disabled={otpIsSmsLoading || otpIsVerifyLoading || saving || isUploading}
                              >
                                {otpStep === "phone"
                                  ? otpIsSmsLoading
                                    ? "Gönderiliyor…"
                                    : "SMS Gönder"
                                  : otpIsVerifyLoading
                                    ? "Doğrulanıyor…"
                                    : "Doğrula"}
                              </Button>

                              {otpStep === "code" ? (
                                <Button
                                  color="secondary"
                                  type="button"
                                  onClick={() => {
                                    setOtpStep("phone");
                                    setOtpSmsCode("");
                                    setOtpError("");
                                  }}
                                  disabled={otpIsSmsLoading || otpIsVerifyLoading || saving || isUploading}
                                >
                                  Geri
                                </Button>
                              ) : null}
                            </div>
                          ) : null}

                          <small className="text-muted d-block mt-2">
                            Telefon doğrulaması zorunlu değildir.
                          </small>
                        </>
                      ) : (
                        <>
                          <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                            <div className="border rounded p-2" style={{ flex: "1 1 240px" }}>
                              <Label className="mb-0 d-flex align-items-start" style={{ cursor: saving ? "not-allowed" : "pointer" }}>
                                <Input
                                  type="checkbox"
                                  checked={Boolean(publicShare.fullName)}
                                  onChange={(e) => {
                                    publicShareDirtyRef.current = true;
                                    setPublicShare((p) => ({ ...p, fullName: Boolean(e?.target?.checked) }));
                                  }}
                                  disabled={saving || isUploading}
                                  className="mr-2"
                                  style={{ marginTop: 2 }}
                                />
                                <span>
                                  <strong>Adı Soyadı</strong>
                                  <small className="text-muted d-block">
                                    {personalInfoPreview.fullName}
                                  </small>
                                </span>
                              </Label>
                            </div>

                            <div className="border rounded p-2" style={{ flex: "1 1 240px" }}>
                              <Label className="mb-0 d-flex align-items-start" style={{ cursor: saving ? "not-allowed" : "pointer" }}>
                                <Input
                                  type="checkbox"
                                  checked={Boolean(publicShare.phone)}
                                  onChange={(e) => {
                                    publicShareDirtyRef.current = true;
                                    setPublicShare((p) => ({ ...p, phone: Boolean(e?.target?.checked) }));
                                  }}
                                  disabled={saving || isUploading}
                                  className="mr-2"
                                  style={{ marginTop: 2 }}
                                />
                                <span>
                                  <strong>Telefon</strong>
                                  <small className="text-muted d-block">
                                    {personalInfoPreview.phone}
                                  </small>
                                </span>
                              </Label>
                            </div>

                            <div className="border rounded p-2" style={{ flex: "1 1 240px" }}>
                              <Label className="mb-0 d-flex align-items-start" style={{ cursor: saving ? "not-allowed" : "pointer" }}>
                                <Input
                                  type="checkbox"
                                  checked={Boolean(publicShare.email)}
                                  onChange={(e) => {
                                    publicShareDirtyRef.current = true;
                                    setPublicShare((p) => ({ ...p, email: Boolean(e?.target?.checked) }));
                                  }}
                                  disabled={saving || isUploading}
                                  className="mr-2"
                                  style={{ marginTop: 2 }}
                                />
                                <span>
                                  <strong>E-posta</strong>
                                  <small className="text-muted d-block">
                                    {personalInfoPreview.email}
                                  </small>
                                </span>
                              </Label>
                            </div>

                            <div className="border rounded p-2" style={{ flex: "1 1 240px" }}>
                              <Label className="mb-0 d-flex align-items-start" style={{ cursor: saving ? "not-allowed" : "pointer" }}>
                                <Input
                                  type="checkbox"
                                  checked={Boolean(publicShare.address)}
                                  onChange={(e) => {
                                    publicShareDirtyRef.current = true;
                                    setPublicShare((p) => ({ ...p, address: Boolean(e?.target?.checked) }));
                                  }}
                                  disabled={saving || isUploading}
                                  className="mr-2"
                                  style={{ marginTop: 2 }}
                                />
                                <span>
                                  <strong>Adres</strong>
                                  <small className="text-muted d-block">
                                    {personalInfoPreview.address}
                                  </small>
                                </span>
                              </Label>
                            </div>
                          </div>

                          <small className="text-muted d-block mt-2">
                            Seçili alanlar efsane incele sayfasında görüntülenir.
                          </small>
                        </>
                      )}
                    </div>
                  ) : null}

                  <hr className="my-4" />

                  {!readOnly && !isNew && isAdmin ? (
                    <Button
                      color="danger"
                      outline
                      type="button"
                      onClick={onHideLegend}
                      disabled={saving || isUploading}
                      className={embedded ? "btn-block mb-3" : "mb-3"}
                    >
                      Sil (Gizle)
                    </Button>
                  ) : null}

                  {showLegendRulesConsent ? (
                    <div className="mb-3">
                      <Label className="mb-0 d-flex align-items-start" style={{ cursor: saving ? "not-allowed" : "pointer" }}>
                        <Input
                          type="checkbox"
                          checked={Boolean(rulesAccepted)}
                          onChange={(e) => setRulesAccepted(Boolean(e?.target?.checked))}
                          disabled={saving || isUploading}
                          className="mr-2"
                          style={{ marginTop: 3 }}
                        />
                        <span style={{ lineHeight: 1.4 }}>
                          <Button
                            type="button"
                            color="link"
                            className="p-0"
                            onClick={(evt) => {
                              evt.preventDefault();
                              setIsRulesModalOpen(true);
                            }}
                            disabled={saving || isUploading}
                            style={{ fontWeight: 600, verticalAlign: "baseline" }}
                          >
                            Yeni Efsane Ekleme Kuralları
                          </Button>
                          <span>’nı okudum, onaylıyorum.</span>
                        </span>
                      </Label>
                    </div>
                  ) : null}

                  {!readOnly ? (
                    <Button
                      color="primary"
                      type="submit"
                      disabled={saving || isUploading || !canEdit || (isNew && !readOnly && !rulesAccepted)}
                      className={embedded ? "btn-block" : undefined}
                    >
                      {saving ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Kaydediliyor...
                        </>
                      ) : (
                        (embedded ? "Onaya Gönder" : "Kaydet")
                      )}
                    </Button>
                  ) : null}
                  </fieldset>
                </Form>
              </CardBody>
            </Card>
          </Col>

          {!embedded && !isNew && authorInfo && (readOnly || isAdmin) && (
            <Col xl="4">
              <Card className="shadow mb-4">
                <CardHeader className="border-0">
                  <h3 className="mb-0">
                    <i className="ni ni-single-02 mr-2 text-primary" />
                    Efsaneyi Yazan
                  </h3>
                </CardHeader>
                <CardBody>
                  <div style={{ fontSize: 14 }}>
                    {(() => {
                      const publicAuthor = form?.publicAuthor && typeof form.publicAuthor === "object" ? form.publicAuthor : null;
                      if (publicAuthor && Object.keys(publicAuthor).length > 0) {
                        return (
                          <>
                            {publicAuthor.fullName ? (
                              <div className="mb-2">
                                <strong>
                                  <i className="ni ni-badge mr-1" />
                                  Adı Soyadı:
                                </strong>
                                <br />
                                <span>{publicAuthor.fullName}</span>
                              </div>
                            ) : null}
                            {publicAuthor.phone ? (
                              <div className="mb-2">
                                <strong>
                                  <i className="ni ni-mobile-button mr-1" />
                                  Telefon:
                                </strong>
                                <br />
                                <span>{publicAuthor.phone}</span>
                              </div>
                            ) : null}
                            {publicAuthor.email ? (
                              <div className="mb-2">
                                <strong>
                                  <i className="ni ni-email-83 mr-1" />
                                  E-posta:
                                </strong>
                                <br />
                                <span>{publicAuthor.email}</span>
                              </div>
                            ) : null}
                            {publicAuthor.address ? (
                              <div className="mb-2">
                                <strong>
                                  <i className="ni ni-pin-3 mr-1" />
                                  Adres:
                                </strong>
                                <br />
                                <span>{publicAuthor.address}</span>
                              </div>
                            ) : null}
                          </>
                        );
                      }

                      const legacyAll = authorInfo?.publishProfileOnLegends === true;
                      const share = {
                        photo: authorInfo?.legendSharePhoto === true || legacyAll,
                        fullName: authorInfo?.legendShareFullName === true || legacyAll,
                        gorev: authorInfo?.legendShareGorev === true || legacyAll,
                        email: authorInfo?.legendShareEmail === true || legacyAll,
                        phone: authorInfo?.legendSharePhone === true || legacyAll,
                        address: authorInfo?.legendShareAddress === true || legacyAll,
                      };

                      const nameValue = authorInfo.fullName || authorInfo.displayName || "";
                      const hasAnyValue =
                        (share.photo && !!authorInfo.photoURL) ||
                        (share.fullName && !!nameValue) ||
                        (share.gorev && !!authorInfo.gorev) ||
                        (share.email && !!authorInfo.email) ||
                        (share.phone && !!authorInfo.phone) ||
                        (share.address && !!authorInfo.address);

                      const hasAnyShareSelected = Object.values(share).some(Boolean);
                      if (!hasAnyShareSelected || !hasAnyValue) {
                        return (
                          <p className="text-muted mb-0">Kişi bilgileri paylaşılmamıştır.</p>
                        );
                      }

                      return (
                        <>
                          {share.photo && authorInfo.photoURL ? (
                            <div className="text-center mb-3">
                              <img
                                src={authorInfo.photoURL}
                                alt="Yazar"
                                className="rounded-circle"
                                style={{
                                  width: 80,
                                  height: 80,
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ) : null}

                          {share.fullName && nameValue ? (
                            <div className="mb-2">
                              <strong>
                                <i className="ni ni-badge mr-1" />
                                Adı Soyadı:
                              </strong>
                              <br />
                              <span>{nameValue}</span>
                            </div>
                          ) : null}

                          {share.gorev && authorInfo.gorev ? (
                            <div className="mb-2">
                              <strong>
                                <i className="ni ni-briefcase-24 mr-1" />
                                Görevi:
                              </strong>
                              <br />
                              <span>{authorInfo.gorev}</span>
                            </div>
                          ) : null}

                          {share.email && authorInfo.email ? (
                            <div className="mb-2">
                              <strong>
                                <i className="ni ni-email-83 mr-1" />
                                E-posta:
                              </strong>
                              <br />
                              <span>{authorInfo.email}</span>
                            </div>
                          ) : null}

                          {share.phone && authorInfo.phone ? (
                            <div className="mb-2">
                              <strong>
                                <i className="ni ni-mobile-button mr-1" />
                                Telefon:
                              </strong>
                              <br />
                              <span>{authorInfo.phone}</span>
                            </div>
                          ) : null}

                          {share.address && authorInfo.address ? (
                            <div className="mb-2">
                              <strong>
                                <i className="ni ni-pin-3 mr-1" />
                                Adres:
                              </strong>
                              <br />
                              <span>{authorInfo.address}</span>
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </Container>

      <Modal isOpen={isSavedInfoOpen} toggle={handleSavedInfoClose} centered>
        <ModalBody className="text-center">
          <p className="mb-4">
            Efsaneniz kayıt edilmiştir, site yöneticisi onayladığında efsane listesinde yayınlanacaktır.
          </p>
          <Button color="primary" onClick={handleSavedInfoClose}>
            Tamam
          </Button>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isRulesModalOpen}
        toggle={() => setIsRulesModalOpen(false)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setIsRulesModalOpen(false)}>
          Yeni Efsane Ekleme Kuralları
        </ModalHeader>
        <ModalBody>
          {legendRulesContent}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            type="button"
            onClick={() => {
              setRulesAccepted(true);
              setIsRulesModalOpen(false);
            }}
          >
            Okudum, anladım
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

